import VideoContext, { SOURCENODESTATE } from "../../videocontext";
import { parseGIF, decompressFrames } from 'gifuct-js'

const TYPE = "GiffNode";
const FRAME_TIME = 100;

class GiffNode extends VideoContext.NODES.SourceNode {
    constructor(src, gl, renderGraph, currentTime, preloadTime = 4) {
        super(src, gl, renderGraph, currentTime);
        this._preloadTime = preloadTime;
        this._textureUploaded = false;
        this._displayName = TYPE;
        this._frames = null;
        this._currentFrame = 0;
        this._ctx = document.createElement('canvas').getContext('2d')
        this._element = this._ctx.canvas;
        this._tempCtx = document.createElement('canvas').getContext('2d')
        this._tempCanvas = this._tempCtx.canvas;
        this._imageData = null;
    }

    get elementURL() {
        return this._elementURL;
    }

    _load() {
        if (this._loadCalled || this._frames) {
            return;
        }
        super._load();
        const xmlHttpRequest = new XMLHttpRequest()
        xmlHttpRequest.open('GET', this._elementURL, true);
        xmlHttpRequest.responseType = 'arraybuffer'
        xmlHttpRequest.onload = () => {
            const arrayBuffer = xmlHttpRequest.response;
            if (arrayBuffer) {
                const gif = parseGIF(arrayBuffer);
                this._frames = decompressFrames(gif, true);
                this._currentFrame = 0
                this._ready = true;

                this._element.width = this._frame.dims.width
                this._element.height = this._frame.dims.height

                this._drawAllToCurrentTime();

                this._triggerCallbacks("loaded");
            }
        }

        xmlHttpRequest.send(null)
    }

    _unload() {
        this._frames = null;
        this._ready = false;
        this._ctx.clearRect(0,0, this._element.width, this._element.height)
        super._unload();
    }

    get _frame() {
        return this._frames[this._currentFrame]
    }

    _nextFrame() {
        if (this._frames.length - 1 > this._currentFrame) {
            this._currentFrame++;
        } else {
            this._currentFrame = 0;
        }
    }

    _drawAllToCurrentTime() {
        this.updateCurrentFrameIndexFromTime(this._currentTime)

        const targetFrame = this._currentFrame;

        this._currentFrame = 0;
        this._imageData = null;

        while (this._currentFrame < targetFrame) {
            this._drawFrame();
            this._nextFrame();
        }
    }

    _seek(time) {
        super._seek(time);

        if (!this._frames) {
            return;
        }

        this._drawAllToCurrentTime();
    }

    _drawFrame() {
        try {
            if (this._frame.disposalType === 2) {
                this._ctx.clearRect(0,0, this._element.width, this._element.height)
            }

            if (!this._imageData || this._frame.dims.width !== this._imageData.width || this._frame.dims.height !== this._imageData.height) {
                this._tempCanvas.width = this._frame.dims.width;
                this._tempCanvas.height = this._frame.dims.height;
                this._imageData = this._tempCtx.createImageData(this._frame.dims.width, this._frame.dims.height)
            }

            this._imageData.data.set(this._frame.patch)

            this._tempCtx.putImageData(this._imageData, 0, 0)
            this._ctx.drawImage(this._tempCanvas, this._frame.dims.left, this._frame.dims.top)
        } catch (error) {
            console.log(this._frames, this._currentFrame)
            console.error(error)
        }
    }

    // e.g. currentTime = 1.2358745885;
    updateCurrentFrameIndexFromTime(currentVideoTime) {
        const currentTime = currentVideoTime - this.startTime
        let currentIndex = 0
        if (currentVideoTime > this.stopTime) {
            currentIndex = this._frames.length - 1;
        } else {
            const currentMS = currentTime * 1000 // to MS
            let calculatedTime = 0;
            for(const frameIndex in this._frames) {
                if (currentMS <= calculatedTime) {
                    break;
                }
                currentIndex = frameIndex;
                calculatedTime += this._frames[frameIndex].delay || FRAME_TIME
            }
        }

        this._currentFrame = currentIndex;
    }

    _update(currentTime) {
        super._update(currentTime, !this._textureUploaded);

        if (
            this._startTime - this._currentTime <= this._preloadTime &&
            this._state !== SOURCENODESTATE.waiting &&
            this._state !== SOURCENODESTATE.ended
        ) {
            this._load();
        }

        if (this._state === SOURCENODESTATE.ended && this._frames) {
            return this._unload();
        }

        if (!this._ready) {
            return;
        }

        this.updateCurrentFrameIndexFromTime(currentTime)
        this._drawFrame();
    }
}

export { FRAME_TIME };

export default GiffNode;